import { BitmapText, Container, Sprite, type Texture } from 'pixi.js';
import { motion } from '../animation/Motion';
import { BITMAP_FONT } from '../rendering/fonts';

interface Particle {
  sprite: Sprite;
  vx: number;
  vy: number;
  spin: number;
  life: number;
  maxLife: number;
  gravity: number;
  baseScale: number;
}

/**
 * Fixed-size particle pool for paper scraps, eraser dust and pencil
 * shavings. Sprites are created once and recycled; every particle shares the
 * atlas texture so they batch into a single draw call.
 */
export class ParticlePool extends Container {
  private particles: Particle[] = [];
  private next = 0;

  constructor(size: number) {
    super();
    for (let i = 0; i < size; i++) {
      const sprite = new Sprite();
      sprite.anchor.set(0.5);
      sprite.visible = false;
      this.addChild(sprite);
      this.particles.push({ sprite, vx: 0, vy: 0, spin: 0, life: 0, maxLife: 1, gravity: 0, baseScale: 1 });
    }
  }

  burst(textures: Texture[], x: number, y: number, count: number, opts: { speed?: number; gravity?: number; scale?: number; life?: number; up?: boolean } = {}): void {
    if (motion.reduced) count = Math.min(count, 3);
    const { speed = 260, gravity = 700, scale = 0.5, life = 900, up = true } = opts;
    for (let i = 0; i < count; i++) {
      const p = this.particles[this.next]!;
      this.next = (this.next + 1) % this.particles.length;
      const a = up ? -Math.PI / 2 + (Math.random() - 0.5) * 2.4 : Math.random() * Math.PI * 2;
      const v = speed * (0.4 + Math.random() * 0.8);
      p.sprite.texture = textures[i % textures.length]!;
      p.sprite.position.set(x, y);
      p.sprite.rotation = Math.random() * Math.PI * 2;
      p.sprite.alpha = 1;
      p.sprite.visible = true;
      p.vx = Math.cos(a) * v;
      p.vy = Math.sin(a) * v;
      p.spin = (Math.random() - 0.5) * 8;
      p.gravity = gravity;
      p.maxLife = life * (0.7 + Math.random() * 0.6);
      p.life = p.maxLife;
      // `scale` is relative to a 64px sprite, whatever the art's real resolution.
      p.baseScale = scale * (64 / Math.max(1, p.sprite.texture.width)) * (0.6 + Math.random() * 0.7);
      p.sprite.scale.set(p.baseScale);
    }
  }

  update(dt: number): void {
    const s = dt / 1000;
    for (const p of this.particles) {
      if (p.life <= 0) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.sprite.visible = false;
        continue;
      }
      p.vy += p.gravity * s;
      p.vx *= 0.985;
      p.sprite.x += p.vx * s;
      p.sprite.y += p.vy * s;
      p.sprite.rotation += p.spin * s;
      const k = p.life / p.maxLife;
      p.sprite.alpha = Math.min(1, k * 2);
      p.sprite.scale.set(p.baseScale * (0.6 + 0.4 * k));
    }
  }

  get active(): number {
    let n = 0;
    for (const p of this.particles) if (p.life > 0) n++;
    return n;
  }
}

interface FloatText {
  text: BitmapText;
  life: number;
  maxLife: number;
  vy: number;
}

/** Pooled floating numbers ("-12", "MISS", "+5") using one bitmap font. */
export class FloatingNumbers extends Container {
  private items: FloatText[] = [];

  constructor(size = 10) {
    super();
    for (let i = 0; i < size; i++) {
      const text = new BitmapText({ text: '', style: { fontFamily: BITMAP_FONT, fontSize: 48 } });
      text.anchor.set(0.5);
      text.visible = false;
      this.addChild(text);
      this.items.push({ text, life: 0, maxLife: 1, vy: 0 });
    }
  }

  show(str: string, x: number, y: number, color: number, size = 48): void {
    const item = this.items.find((i) => i.life <= 0) ?? this.items[0]!;
    item.text.text = str;
    item.text.tint = color;
    item.text.style.fontSize = size;
    item.text.position.set(x + (Math.random() - 0.5) * 24, y);
    item.text.rotation = (Math.random() - 0.5) * 0.25;
    item.text.alpha = 1;
    item.text.scale.set(motion.reduced ? 1 : 0.3);
    item.text.visible = true;
    item.maxLife = 1100;
    item.life = item.maxLife;
    item.vy = -70;
  }

  update(dt: number): void {
    const s = dt / 1000;
    for (const it of this.items) {
      if (it.life <= 0) continue;
      it.life -= dt;
      if (it.life <= 0) {
        it.text.visible = false;
        continue;
      }
      const age = 1 - it.life / it.maxLife;
      if (!motion.reduced) {
        it.text.y += it.vy * s;
        const pop = age < 0.15 ? 0.3 + (age / 0.15) * 0.95 : 1.25 - Math.min(0.25, (age - 0.15) * 1.5);
        it.text.scale.set(pop);
      }
      it.text.alpha = age > 0.7 ? 1 - (age - 0.7) / 0.3 : 1;
    }
  }
}
